import { useEffect, useMemo, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase';

const storageUrlCache = new Map();

export function createVersionToken() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function getCachedStorageUrl(path = '') {
  const cached = storageUrlCache.get(String(path || '').trim());
  return typeof cached === 'string' ? cached : '';
}

export function clearCachedStorageUrl(path = '') {
  storageUrlCache.delete(String(path || '').trim());
}

export async function resolveStorageUrl(path = '') {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return '';
  const cached = storageUrlCache.get(normalizedPath);
  if (typeof cached === 'string') return cached;
  if (cached) return cached;
  const pending = getDownloadURL(ref(storage, normalizedPath))
    .then((url) => {
      storageUrlCache.set(normalizedPath, url);
      return url;
    })
    .catch((error) => {
      storageUrlCache.delete(normalizedPath);
      throw error;
    });
  storageUrlCache.set(normalizedPath, pending);
  return pending;
}

export function useStorageUrl(path = '', { enabled = true } = {}) {
  const normalizedPath = String(path || '').trim();
  const [url, setUrl] = useState(() => (enabled ? getCachedStorageUrl(normalizedPath) : ''));

  useEffect(() => {
    let active = true;
    if (!enabled || !normalizedPath) {
      setUrl('');
      return () => {
        active = false;
      };
    }
    const cached = getCachedStorageUrl(normalizedPath);
    if (cached) {
      setUrl(cached);
      return () => {
        active = false;
      };
    }
    resolveStorageUrl(normalizedPath)
      .then((nextUrl) => {
        if (active) setUrl(nextUrl);
      })
      .catch(() => {
        if (active) setUrl('');
      });
    return () => {
      active = false;
    };
  }, [enabled, normalizedPath]);

  return url;
}

export function useStorageUrls(paths = []) {
  const normalizedPaths = useMemo(
    () => (Array.isArray(paths) ? paths.map((entry) => String(entry || '').trim()) : []),
    [paths]
  );
  const cacheKey = normalizedPaths.join('|');
  const [urls, setUrls] = useState(() => Object.fromEntries(
    normalizedPaths.filter(Boolean).map((path) => [path, getCachedStorageUrl(path)])
  ));

  useEffect(() => {
    let active = true;
    const nextUrls = Object.fromEntries(
      normalizedPaths.filter(Boolean).map((path) => [path, getCachedStorageUrl(path)])
    );
    setUrls(nextUrls);
    const pending = normalizedPaths.filter((path) => path && !nextUrls[path]);
    if (!pending.length) {
      return () => {
        active = false;
      };
    }
    Promise.all(pending.map(async (path) => [path, await resolveStorageUrl(path)]))
      .then((resolvedEntries) => {
        if (!active) return;
        setUrls((prev) => ({
          ...prev,
          ...Object.fromEntries(resolvedEntries),
        }));
      })
      .catch(() => {
        if (active) setUrls((prev) => ({ ...prev }));
      });
    return () => {
      active = false;
    };
  }, [cacheKey, normalizedPaths]);

  return urls;
}

export function getArtistPhotoPath(artist = {}, size = 'thumb') {
  if (size === 'tiny') return String(artist?.artistProfilePhoto50Path || '').trim();
  if (size === 'full') return String(artist?.artistProfilePhotoPath || artist?.artistProfilePhoto1000Path || '').trim();
  return String(artist?.artistProfilePhotoThumbPath || artist?.artistProfilePhoto200Path || artist?.artistProfilePhotoPath || '').trim();
}

export function getArtistSignaturePath(artist = {}) {
  return String(artist?.artistSignaturePath || artist?.artistSignature1000Path || '').trim();
}

export function getUserPhotoPath(user = {}, size = 'thumb') {
  if (size === 'full') return String(user?.photoPath || '').trim();
  return String(user?.photoThumbPath || user?.photoPath || '').trim();
}

export function getPrimaryMedia(record = {}) {
  return Array.isArray(record?.media) ? record.media[0] || null : null;
}

export function getPrimaryImagePath(record = {}) {
  const media = getPrimaryMedia(record);
  return String(media?.storagePath || '').trim();
}
